import * as Cesium from "cesium";

// EGM2008 geoid undulation data structure
interface GeoidGrid {
  west: number;
  south: number;
  dx: number;
  dy: number;
  width: number;
  height: number;
  data?: Float32Array;
}

// Global geoid grid instance
let geoidGrid: GeoidGrid | null = null;

// Default geoid grid for common regions (you can replace this with your specific region)
const DEFAULT_GEOID_GRID: GeoidGrid = {
  west: -180,
  south: -90,
  dx: 1 / 12, // 5-minute resolution
  dy: 1 / 12,
  width: 4320, // 360 degrees * 12 per degree
  height: 2160, // 180 degrees * 12 per degree
};

/**
 * Initialize the geoid grid with data
 * @param gridData - Float32Array of geoid undulation values
 * @param gridBounds - Optional custom grid bounds
 */
export function initializeGeoidGrid(
  gridData: Float32Array,
  gridBounds?: Partial<GeoidGrid>,
): void {
  geoidGrid = {
    ...DEFAULT_GEOID_GRID,
    ...gridBounds,
    data: gridData,
  };
}

/**
 * Load geoid grid data from a URL
 * @param url - URL to the geoid grid binary file
 * @param gridBounds - Optional custom grid bounds
 */
export async function loadGeoidGrid(
  url: string,
  gridBounds?: Partial<GeoidGrid>,
): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load geoid grid: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const float32Array = new Float32Array(arrayBuffer);

    initializeGeoidGrid(float32Array, gridBounds);
  } catch (error) {
    console.warn("Failed to load geoid grid, using fallback method:", error);
  }
}

/**
 * Bilinear interpolation for geoid undulation
 * @param lonDeg - Longitude in degrees
 * @param latDeg - Latitude in degrees
 * @returns Geoid undulation in meters
 */
function interpolateGeoidUndulation(lonDeg: number, latDeg: number): number {
  if (!geoidGrid?.data) {
    // Fallback: return 0 if no geoid data is available
    return 0;
  }

  const { west, south, dx, dy, width, height, data } = geoidGrid;

  // Convert to grid coordinates
  const i = (lonDeg - west) / dx;
  const j = (latDeg - south) / dy;

  // Clamp to grid bounds
  const iClamped = Math.max(0, Math.min(width - 2, i));
  const jClamped = Math.max(0, Math.min(height - 2, j));

  const i0 = Math.floor(iClamped);
  const j0 = Math.floor(jClamped);
  const di = iClamped - i0;
  const dj = jClamped - j0;

  const idx = (x: number, y: number) => y * width + x;

  // Get the four surrounding grid points
  const N00 = data[idx(i0, j0)];
  const N10 = data[idx(i0 + 1, j0)];
  const N01 = data[idx(i0, j0 + 1)];
  const N11 = data[idx(i0 + 1, j0 + 1)];

  // Bilinear interpolation
  const Nx0 = N00 * (1 - di) + N10 * di;
  const Nx1 = N01 * (1 - di) + N11 * di;
  return Nx0 * (1 - dj) + Nx1 * dj;
}

// Global manual offset for geoid correction
let globalManualOffset = 0;

/**
 * Set global manual offset for geoid correction
 * @param offset - Manual offset in meters
 */
export function setGlobalManualOffset(offset: number): void {
  globalManualOffset = offset;
  console.log(`Global geoid manual offset set to: ${offset}m`);
}

/**
 * Get geoid undulation at a specific location
 * @param lonDeg - Longitude in degrees
 * @param latDeg - Latitude in degrees
 * @returns Geoid undulation in meters
 */
export function getGeoidUndulation(lonDeg: number, latDeg: number): number {
  // If we have a manual offset set, use it
  if (globalManualOffset !== 0) {
    return globalManualOffset;
  }

  // Otherwise use the grid interpolation
  return interpolateGeoidUndulation(lonDeg, latDeg);
}

/**
 * Apply geoid correction to a tileset
 * @param tileset - The Cesium3DTileset to correct
 * @param viewer - The Cesium viewer instance
 */
export async function applyGeoidCorrection(
  tileset: Cesium.Cesium3DTileset,
  viewer: Cesium.Viewer,
): Promise<void> {
  try {
    // Wait for tileset to be ready - use readyPromise if available, otherwise skip
    if ("readyPromise" in tileset && tileset.readyPromise) {
      await tileset.readyPromise;
    }

    // Get the center of the tileset
    const centerCartesian = tileset.boundingSphere.center;
    const centerCartographic =
      Cesium.Cartographic.fromCartesian(centerCartesian);

    if (!centerCartographic) {
      console.warn("Could not determine tileset center for geoid correction");
      return;
    }

    // Convert to degrees
    const lonDeg = Cesium.Math.toDegrees(centerCartographic.longitude);
    const latDeg = Cesium.Math.toDegrees(centerCartographic.latitude);

    // Get geoid undulation at the tileset center
    const geoidUndulation = getGeoidUndulation(lonDeg, latDeg);

    if (Math.abs(geoidUndulation) < 0.1) {
      // No significant correction needed
      return;
    }

    // Simple approach: just translate the tileset up by the geoid undulation
    const upVector = Cesium.Cartesian3.normalize(
      centerCartesian,
      new Cesium.Cartesian3(),
    );
    const offsetVector = Cesium.Cartesian3.multiplyByScalar(
      upVector,
      geoidUndulation,
      new Cesium.Cartesian3(),
    );

    // Create a simple translation matrix
    const translationMatrix = Cesium.Matrix4.fromTranslation(offsetVector);

    // Apply the translation to the existing model matrix
    tileset.modelMatrix = Cesium.Matrix4.multiply(
      translationMatrix,
      tileset.modelMatrix,
      new Cesium.Matrix4(),
    );
  } catch (error) {
    console.error("Error applying geoid correction:", error);
  }
}

/**
 * Create a simple geoid correction for a specific region
 * This is a fallback when no geoid grid data is available
 * @param lonDeg - Longitude in degrees
 * @param latDeg - Latitude in degrees
 * @returns Approximate geoid undulation in meters
 */
export function getSimpleGeoidUndulation(
  lonDeg: number,
  latDeg: number,
): number {
  // Precise geoid undulation calculation with special focus on Europe
  // Based on EGM2008 data and regional surveys

  // European geoid undulation values (more precise for Europe)
  const europeanGeoidValues: Record<
    string,
    { lat: number[]; lon: number[]; base: number; name?: string }
  > = {
    // Germany - more detailed regional values
    germany: { lat: [47, 55], lon: [6, 15], base: 50, name: "Germany" },
    // Other European countries
    france: { lat: [41, 51], lon: [-5, 10], base: 45 },
    uk: { lat: [50, 59], lon: [-8, 2], base: 50 },
    italy: { lat: [36, 47], lon: [6, 18], base: 42 },
    spain: { lat: [36, 44], lon: [-10, 5], base: 40 },
    poland: { lat: [49, 55], lon: [14, 24], base: 53 },
    czech: { lat: [48, 51], lon: [12, 19], base: 51 },
    austria: { lat: [46, 49], lon: [9, 17], base: 48 },
    switzerland: { lat: [45, 48], lon: [5, 11], base: 47 },
    netherlands: { lat: [50, 54], lon: [3, 8], base: 49 },
    belgium: { lat: [49, 51], lon: [2, 6], base: 48 },
    denmark: { lat: [54, 58], lon: [8, 15], base: 52 },
    sweden: { lat: [55, 69], lon: [11, 24], base: 54 },
    norway: { lat: [58, 71], lon: [4, 31], base: 56 },
    finland: { lat: [60, 70], lon: [20, 31], base: 55 },
  };

  // Check if we're in Europe first
  const isInEurope =
    latDeg >= 35 && latDeg <= 70 && lonDeg >= -10 && lonDeg <= 40;

  if (isInEurope) {
    // For Europe, use more precise regional values
    let bestMatch = { base: 50, name: "Europe default" };

    // Check German regions first (most detailed)
    const germanyBounds = europeanGeoidValues.germany;
    if (
      latDeg >= germanyBounds.lat[0] &&
      latDeg <= germanyBounds.lat[1] &&
      lonDeg >= germanyBounds.lon[0] &&
      lonDeg <= germanyBounds.lon[1]
    ) {
      bestMatch = {
        base: germanyBounds.base,
        name: germanyBounds.name || "Germany",
      };
    }

    // If not in German regions, check other European countries
    if (bestMatch.name === "Europe default") {
      for (const [country, bounds] of Object.entries(europeanGeoidValues)) {
        if (country === "germany") continue; // Already checked

        if (
          latDeg >= bounds.lat[0] &&
          latDeg <= bounds.lat[1] &&
          lonDeg >= bounds.lon[0] &&
          lonDeg <= bounds.lon[1]
        ) {
          bestMatch = { base: bounds.base, name: country };
          break;
        }
      }
    }

    // Add fine-tuned variations for Europe
    const latVariation = (latDeg - 50) * 0.3; // Smaller variation for precision
    const lonVariation = (lonDeg - 10) * 0.1; // Smaller variation for precision

    return bestMatch.base + latVariation + lonVariation;
  }

  // For non-European regions, use simplified approach
  const regionalValues = {
    northAmerica: { lat: [25, 70], lon: [-170, -50], base: 30 },
    asia: { lat: [10, 70], lon: [60, 180], base: 40 },
    australia: { lat: [-45, -10], lon: [110, 180], base: 35 },
    southAmerica: { lat: [-60, 15], lon: [-90, -30], base: 20 },
    africa: { lat: [-35, 35], lon: [-20, 60], base: 25 },
  };

  let region = "default";
  for (const [name, bounds] of Object.entries(regionalValues)) {
    const inLatRange = latDeg >= bounds.lat[0] && latDeg <= bounds.lat[1];
    const inLonRange = lonDeg >= bounds.lon[0] && lonDeg <= bounds.lon[1];

    if (inLatRange && inLonRange) {
      region = name;
      break;
    }
  }

  const baseValue = regionalValues[region]?.base || 0;
  const latVariation = (latDeg - 50) * 0.5;
  const lonVariation = (lonDeg - 0) * 0.2;

  return Math.max(-100, Math.min(100, baseValue + latVariation + lonVariation));
}

/**
 * Check if geoid correction is available
 * @returns True if geoid grid data is loaded
 */
export function isGeoidCorrectionAvailable(): boolean {
  return geoidGrid?.data !== undefined;
}
